<template>
  <div class="bg-app vh-100 dashboard">
    <VuePullRefresh
      :key="pullKey"
      :on-refresh="appRefresh"
      :config="{
        startLabel: '',
        readyLabel: '',
        loadingLabel: '',
        pullDownHeight: 100,
        maxHeight: 120,
      }"
    >
      <v-container class="py-0 pb-10 mt-6" fluid :class="$vuetify.breakpoint.mobile ? 'px-3' : 'px-4'">
        <v-alert
          v-if="!$online && $networkInitialized"
          dense
          tile
          color="gray"
          icon="mdi-alert-circle-outline"
          class="alert-offline text-headline"
        >
          {{ $t("t_no_connection") }}
        </v-alert>

        <!-- Quick Access Grid -->
        <v-row class="quick-access-row">
          <!-- Time Clock Card -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-card
              class="quick-access-card quick-access-card--timeclock rounded-lg text-center pa-4 elevation-2"
              @click="$router.push({ name: 'timeclock' })"
              ripple
            >
              <div class="position-relative d-inline-block">
                <v-icon size="48" class="section-icon section-icon--timeclock">mdi-clock-time-three-outline</v-icon>
                <v-badge
                  v-if="activeTimeClock"
                  color="success"
                  :content="$t('t_active')"
                  class="mt-1"
                  overlap
                  offset-x="18"
                  offset-y="18"
                ></v-badge>
                <v-badge
                  v-if="hasUnreadTimeClockNotes && !activeTimeClock"
                  overlap
                  offset-x="18"
                  offset-y="18"
                  class="notes-badge"
                  color="info"
                >
                  <template v-slot:badge>
                    <v-icon size="20px">mdi-message-alert-outline</v-icon>
                  </template>
                </v-badge>
              </div>

              <div class="mt-3 text-subtitle-1 font-weight-medium">{{ $t("t_time_clock") }}</div>

              <div v-if="weekTotal && weekTotal !== '0:00'" class="text-caption grey--text mt-1">
                {{ $t("t_week_total") }}: {{ weekTotal }} {{ $t("t_hours") }}
              </div>
              <div v-else class="text-caption grey--text mt-1">{{ $t("t_track_time") }}</div>
            </v-card>
          </v-col>

          <!-- Forms/Assignments Card -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-card
              class="quick-access-card quick-access-card--forms rounded-lg text-center pa-4 elevation-2"
              @click="$router.push({ name: 'forms' })"
              ripple
            >
              <div class="position-relative d-inline-block">
                <v-icon size="48" class="section-icon section-icon--forms">mdi-form-select</v-icon>
                <v-badge
                  v-if="hasDueForms"
                  overlap
                  offset-x="18"
                  offset-y="18"
                  class="due-badge"
                  avatar
                  color="orange darken-2"
                >
                  <template v-slot:badge>
                    <v-avatar>
                      <v-icon size="22px">mdi-clock-time-four-outline</v-icon>
                    </v-avatar>
                  </template>
                </v-badge>
              </div>
              <div class="mt-3 text-subtitle-1 font-weight-medium">{{ $t("t_forms") }}</div>
              <div class="text-caption grey--text mt-1">{{ $t("t_assignments_library") }}</div>
            </v-card>
          </v-col>

          <!-- Messages Card -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-card
              class="quick-access-card quick-access-card--messages rounded-lg text-center pa-4 elevation-2"
              @click="$router.push({ name: 'messages' })"
              ripple
            >
              <div class="position-relative d-inline-block">
                <v-icon size="48" class="section-icon section-icon--messages">mdi-cube-send</v-icon>
                <v-badge
                  v-if="unreadBadgeText"
                  :content="unreadBadgeText"
                  color="error"
                  overlap
                  offset-x="18"
                  offset-y="18"
                  class="unread-badge"
                >
                </v-badge>
              </div>
              <div class="mt-3 text-subtitle-1 font-weight-medium">{{ $t("t_messages") }}</div>
              <div class="text-caption grey--text mt-1">{{ $t("t_communications") }}</div>
            </v-card>
          </v-col>

          <!-- Log-It (Issues) Card -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-card
              class="quick-access-card quick-access-card--issues rounded-lg text-center pa-4 elevation-2"
              @click="$router.push({ name: 'issues' })"
              ripple
            >
              <v-icon size="48" class="section-icon section-icon--issues">mdi-camera-outline</v-icon>
              <div class="mt-3 text-subtitle-1 font-weight-medium">{{ $t("t_log_it") }}</div>
              <div class="text-caption grey--text mt-1">{{ $t("t_issues_photo_logging") }}</div>
            </v-card>
          </v-col>

          <!-- Documents Card -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-card
              class="quick-access-card quick-access-card--documents rounded-lg text-center pa-4 elevation-2"
              @click="$router.push({ name: 'documents' })"
              ripple
            >
              <v-icon size="48" class="section-icon section-icon--documents">mdi-note-multiple-outline</v-icon>
              <div class="mt-3 text-subtitle-1 font-weight-medium">{{ $t("t_documents") }}</div>
              <div class="text-caption grey--text mt-1">{{ $t("t_files_docs") }}</div>
            </v-card>
          </v-col>

          <!-- Planners Card (conditional) -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-card
              class="quick-access-card quick-access-card--planners rounded-lg text-center pa-4 elevation-2"
              @click="$router.push({ name: 'planners' })"
              ripple
            >
              <div class="position-relative d-inline-block">
                <v-icon size="48" class="section-icon section-icon--planners">mdi-calendar-check</v-icon>
                <v-badge
                  v-if="hasDuePlanners"
                  color="orange darken-2"
                  overlap
                  offset-x="18"
                  offset-y="18"
                  class="due-badge"
                  avatar
                >
                  <template v-slot:badge>
                    <v-avatar>
                      <v-icon size="22px">mdi-clock-time-four-outline</v-icon>
                    </v-avatar>
                  </template>
                </v-badge>
              </div>
              <div class="mt-3 text-subtitle-1 font-weight-medium">{{ $t("t_planners") }}</div>
              <div class="text-caption grey--text mt-1">{{ $t("t_tasks_reports") }}</div>
            </v-card>
          </v-col>

          <!-- Training Card -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-card
              class="quick-access-card quick-access-card--training rounded-lg text-center pa-4 elevation-2"
              @click="$router.push({ name: 'training' })"
              ripple
            >
              <v-icon size="48" class="section-icon section-icon--training">mdi-school-outline</v-icon>
              <div class="mt-3 text-subtitle-1 font-weight-medium">{{ $t("t_training") }}</div>
              <div class="text-caption grey--text mt-1">{{ $t("t_courses") }}</div>
            </v-card>
          </v-col>

          <!-- ranesCAST Card -->
          <v-col cols="6" sm="4" md="3" class="quick-access-col">
            <v-tooltip bottom :disabled="!castDisabled">
              <template v-slot:activator="{ on }">
                <v-card
                  class="quick-access-card quick-access-card--cast rounded-lg text-center pa-4 elevation-2"
                  :class="{ 'quick-access-card--disabled': castDisabled }"
                  :href="castUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  ripple
                  v-on="on"
                >
                  <div class="cast-icon-wrapper">
                    <v-icon size="48" color="#e54634">mdi-podcast</v-icon>
                    <v-icon size="12" class="external-link-badge">mdi-open-in-new</v-icon>
                  </div>
                  <div class="mt-3 text-subtitle-1 font-weight-medium">ranesCAST</div>
                  <div class="text-caption grey--text mt-1">
                    {{ $t("t_cast_vod") }}&nbsp;<v-icon size="10" style="vertical-align: baseline; opacity: 0.5;">mdi-open-in-new</v-icon>
                  </div>
                </v-card>
              </template>
              <span>ranesCAST is temporarily unavailable</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <!-- Activity Feed Section -->
        <v-row class="mt-6">
          <v-col cols="12">
            <ActivityFeed ref="activityFeed" :itemsPerCategory="$vuetify.breakpoint.mobile ? 5 : 8" />
          </v-col>
        </v-row>
      </v-container>
    </VuePullRefresh>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Helpers from "@/mixins/helpers";
import ActivityFeed from "@/components/dashboard/ActivityFeed.vue";
import VuePullRefresh from "vue-pull-refresh";
import { MessageService, FormService, PlannerService, TimeClockService, TokenService } from "@/services";
import { DateTime } from "luxon";

export default {
  name: "Dashboard",
  metaInfo: {
    title: "Dashboard",
  },
  mixins: [Helpers],
  components: {
    ActivityFeed,
    VuePullRefresh,
  },
  computed: {
    ...mapGetters("timeClock", ["timeClocks", "activeTimeClock", "timeClockDuration"]),
    ...mapGetters("settings", ["settings"]),
    castDisabled() {
      return !window.appConfig?.VUE_APP_CAST_WEB_URL;
    },
    castUrl() {
      if (!window.appConfig?.VUE_APP_CAST_WEB_URL) return undefined;
      return window.appConfig.VUE_APP_CAST_WEB_URL + "auth?source=app" + `&token=${TokenService.getRefreshToken()}`;
    },
    unreadBadgeText() {
      if (this.unreadMessagesCount === 0) return null;
      if (this.unreadMessagesCount >= 10) return "9+";
      return this.unreadMessagesCount.toString();
    },
    weekTotal() {
      if (!this.timeClocks?.length) return "0:00";

      const now = DateTime.now().setZone(this.settings?.timeZone || "local");
      const currentWeekStart = now.startOf("week");
      let totalMinutes = 0;

      // Add timeClockDuration as a dependency to make it reactive
      const currentDuration = this.timeClockDuration;

      this.timeClocks.forEach((tc) => {
        const start = DateTime.fromISO(tc.clockStart, { zone: "utc" }).setZone(this.settings?.timeZone || "local");
        const tcWeekStart = start.startOf("week");

        // Only include time clocks from the current week
        if (tcWeekStart.toMillis() !== currentWeekStart.toMillis()) {
          return;
        }

        let duration;
        if (!tc.clockEnd) {
          // For active time clock, use the current duration
          if (this.activeTimeClock && tc.id === this.activeTimeClock.id && currentDuration) {
            const parts = currentDuration.split(":");
            if (parts.length >= 2) {
              const hours = parseInt(parts[0]) || 0;
              const minutes = parseInt(parts[1]) || 0;
              duration = hours * 60 + minutes;
            } else {
              const end = now;
              duration = end.diff(start, "minutes").minutes;
            }
          } else {
            const end = now;
            duration = end.diff(start, "minutes").minutes;
          }
        } else {
          const end = DateTime.fromISO(tc.clockEnd, { zone: "utc" }).setZone(this.settings?.timeZone || "local");
          duration = end.diff(start, "minutes").minutes;
        }

        totalMinutes += Math.max(0, Math.round(duration));
      });

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}:${minutes.toString().padStart(2, "0")}`;
    },
  },
  data: () => ({
    loading: false,
    pullKey: 0,
    unreadMessagesCount: 0,
    hasDueForms: false,
    hasDuePlanners: false,
    hasUnreadTimeClockNotes: false,
  }),
  methods: {
    async loadUnreadMessagesCount() {
      try {
        const params = {
          "paging.orderbyfield": "Id",
          "paging.ascending": false,
          "paging.skipnum": 0,
          "paging.takenum": 8,
        };
        const response = await MessageService.getUnreadMessages(params);
        const count = response.data?.meta?.numTotal || response.data?.length || 0;
        this.unreadMessagesCount = count;
      } catch (error) {
        console.error("Error loading unread messages count:", error);
        this.unreadMessagesCount = 0;
      }
    },

    async loadDueFormsCount() {
      try {
        const now = DateTime.now().toISO();
        const params = {
          CountOnly: true,
          AvailableBefore: now,
          HasExpiresOn: true,
          HasSubmittedSubmission: false,
          "paging.orderbyfield": "Id",
          "paging.ascending": false,
          "paging.skipnum": 0,
          "paging.takenum": 1,
        };
        const response = await FormService.getFormAssignments(params);
        const count = response.data?.count || 0;
        this.hasDueForms = count > 0;
      } catch (error) {
        console.error("Error loading due forms count:", error);
        this.hasDueForms = false;
      }
    },
    async loadDuePlannersCount() {
      try {
        const params = {
          CountType: "due",
          ExcludeDeleted: true,
          ExcludeOpenEnded: false,
          AsOfDate: DateTime.now().toISO(),
        };
        const response = await PlannerService.getPlannerAssignmentsCount(params);
        this.hasDuePlanners = (response.data?.count || 0) > 0;
      } catch (error) {
        console.error("Error loading due planners count:", error);
        this.hasDuePlanners = false;
      }
    },

    async loadUnreadTimeClockNotesCheck() {
      if (!this.$online) return;

      try {
        const now = DateTime.now();
        const recentThreshold = now.minus({ days: 7 }).toISO(); // Check last 7 days

        const params = {
          showOtherUsers: true, // Show notes from other users
          createdAfter: recentThreshold,
          "paging.orderbyfield": "Id",
          "paging.ascending": false,
          "paging.skipnum": 0,
          "paging.takenum": 1, // Only need to check if ANY exist
        };

        const response = await TimeClockService.getTimeClockNotes(params);
        const count = response.meta?.numTotal || response.data?.meta?.numTotal || 0;
        this.hasUnreadTimeClockNotes = count > 0;
      } catch (error) {
        console.error("Error checking timeclock notes:", error);
        this.hasUnreadTimeClockNotes = false;
      }
    },
    appRefresh() {
      this.refresh();
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
          this.pullKey += 1;
        }, 1000);
      });
    },
    refresh() {
      this.loadUnreadMessagesCount();
      this.loadDueFormsCount();
      this.loadDuePlannersCount();
      this.loadUnreadTimeClockNotesCheck();
      this.$refs.activityFeed?.refresh();
    },
  },
  mounted() {
    if (this.$online) {
      this.loadUnreadMessagesCount();
      this.loadDueFormsCount();
      this.loadDuePlannersCount();
      this.loadUnreadTimeClockNotesCheck();
    }
  },
};
</script>

<style lang="scss">
@import "@/scss/variables";

.dashboard {
  .welcome-section {
    padding: 8px 0;
  }

  .quick-access-row {
    @media (max-width: 599.98px) {
      margin: 0 -6px;
      .quick-access-col {
        padding: 6px !important;
      }
    }
  }

  .quick-access-card {
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    padding: 6px !important;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12) !important;
    }

    &:active {
      transform: translateY(-2px);
    }

    // Section-specific borders using variables
    &--timeclock {
      border: 2px solid $color-timeclock;
    }
    &--forms {
      border: 2px solid $color-forms;
    }
    &--messages {
      border: 2px solid $color-messages;
    }
    &--issues {
      border: 2px solid $color-issues;
    }
    &--planners {
      border: 2px solid $color-planners;
    }
    &--documents {
      border: 2px solid $color-documents;
    }
    &--training {
      border: 2px solid $color-training;
    }
    &--cast {
      border: 2px solid #a73326;
    }
    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .cast-icon-wrapper {
    position: relative;
    display: inline-block;

    .external-link-badge {
      position: absolute;
      bottom: 2px;
      right: -4px;
      color: rgba(0, 0, 0, 0.4);
    }
  }

  .section-icon {
    &--timeclock {
      color: $color-timeclock;
    }
    &--forms {
      color: $color-forms;
    }
    &--messages {
      color: $color-messages;
    }
    &--issues {
      color: $color-issues;
    }
    &--planners {
      color: $color-planners;
    }
    &--documents {
      color: $color-documents;
    }
    &--training {
      color: $color-training;
    }
    &--reports {
      color: $color-reports;
    }
  }

  .notes-badge {
    .v-badge__badge {
      border-radius: 4px !important; // Make it square with slight rounding
      min-width: 18px !important;
      width: 18px !important;
      height: 12px !important;
      padding: 0 !important;

      .v-icon {
        font-size: 20px !important;
      }
    }
  }
}

.theme--dark {
  .dashboard {
    .quick-access-card {
      background: rgba(0, 0, 0, 0.9);
    }
  }
}
</style>
